import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { captureEvent, identifyUser, hashString } from '../utils/posthog';
import { EVENTS, PROPERTIES, FORM_FIELDS, ERROR_TYPES, SECTIONS } from '../utils/analytics';
import { useSectionTracking } from '../hooks/useSectionTracking';

// API endpoint is hardcoded below to use Vercel serverless function

export const Waitlist = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');
  const formStartTimeRef = useRef(null);
  const hasFormStartedRef = useRef(false);
  const attemptNumberRef = useRef(0);
  
  // Track section view
  useSectionTracking(SECTIONS.WAITLIST, 'waitlist');
  
  // Track when form section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            captureEvent(EVENTS.WAITLIST_FORM_VIEWED, {
              [PROPERTIES.SECTION_NAME]: SECTIONS.WAITLIST,
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    const waitlistSection = document.getElementById('waitlist');
    if (waitlistSection) {
      observer.observe(waitlistSection);
    }

    return () => {
      if (waitlistSection) {
        observer.unobserve(waitlistSection);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFieldFocus = (fieldName) => {
    // Track form started on first field focus
    if (!hasFormStartedRef.current) {
      hasFormStartedRef.current = true;
      formStartTimeRef.current = Date.now();
      captureEvent(EVENTS.WAITLIST_FORM_STARTED, {
        [PROPERTIES.FORM_FIELD]: fieldName,
      });
    }

    // Track field focus
    captureEvent(EVENTS.WAITLIST_FIELD_FOCUSED, {
      [PROPERTIES.FORM_FIELD]: fieldName,
    });
  };

  const validateForm = () => {
    if (!formData.name || formData.name.length < 2) {
      setErrorMessage('Please enter a valid name (at least 2 characters)');
      captureEvent(EVENTS.WAITLIST_VALIDATION_ERROR, {
        [PROPERTIES.FORM_FIELD]: FORM_FIELDS.NAME,
        [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.VALIDATION_ERROR,
        [PROPERTIES.ERROR_MESSAGE]: 'Name must be at least 2 characters',
      });
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      captureEvent(EVENTS.WAITLIST_VALIDATION_ERROR, {
        [PROPERTIES.FORM_FIELD]: FORM_FIELDS.EMAIL,
        [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.VALIDATION_ERROR,
        [PROPERTIES.ERROR_MESSAGE]: 'Invalid email format',
      });
      return false;
    }
    // More flexible phone validation - accepts Indian and international formats
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,15}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      console.log('Phone validation failed for:', formData.phone);
      setErrorMessage('Please enter a valid phone number (e.g., +91 98765 43210 or 9876543210)');
      captureEvent(EVENTS.WAITLIST_VALIDATION_ERROR, {
        [PROPERTIES.FORM_FIELD]: FORM_FIELDS.PHONE,
        [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.VALIDATION_ERROR,
        [PROPERTIES.ERROR_MESSAGE]: 'Invalid phone number format',
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    // Prevent double submission
    if (isSubmitting) {
      console.log('Form submission already in progress, ignoring click');
      return;
    }
    
    console.log('Form submitted with data:', formData);
    
    // Prevent any scroll to the form/section
    // Remove focus from submit button to prevent scroll
    if (document.activeElement && document.activeElement.tagName === 'BUTTON') {
      document.activeElement.blur();
    }
    
    // Prevent scroll to waitlist section during form submission
    // Store original scrollIntoView to restore it later
    const waitlistSection = document.getElementById('waitlist');
    if (waitlistSection && !waitlistSection._originalScrollIntoView) {
      waitlistSection._originalScrollIntoView = waitlistSection.scrollIntoView.bind(waitlistSection);
      waitlistSection.scrollIntoView = () => {}; // Override scrollIntoView temporarily
      
      // Restore after a short delay
      setTimeout(() => {
        if (waitlistSection._originalScrollIntoView) {
          waitlistSection.scrollIntoView = waitlistSection._originalScrollIntoView;
          delete waitlistSection._originalScrollIntoView;
        }
      }, 1000);
    }
    
    setErrorMessage('');
    setSubmitStatus(null);
    attemptNumberRef.current += 1;

    // Validate form before setting isSubmitting
    if (!validateForm()) {
      console.log('Form validation failed');
      setSubmitStatus('error');
      setIsSubmitting(false); // Ensure isSubmitting is false on validation failure
      // Don't set isSubmitting to true if validation fails
      return;
    }
    
    console.log('Form validation passed, proceeding with API call');

    // Only set isSubmitting after validation passes
    setIsSubmitting(true);

    // Calculate time to submit
    const timeToSubmit = formStartTimeRef.current
      ? Math.round((Date.now() - formStartTimeRef.current) / 1000)
      : 0;

    // Track form submission attempt
    const submitStartTime = Date.now();
    captureEvent(EVENTS.WAITLIST_FORM_SUBMITTED, {
      [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
      [PROPERTIES.TIME_TO_SUBMIT]: timeToSubmit,
    });

    try {
      console.log('Calling waitlist API:', '/api/waitlist/submit', formData);
      // Hardcoded endpoint - always use Vercel serverless function (relative path)
      const response = await axios.post('/api/waitlist/submit', formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('Waitlist API response:', response.data);
      
      const responseTime = Math.round((Date.now() - submitStartTime) / 1000);
      
      if (response.data && response.data.success) {
        // Track successful submission
        captureEvent(EVENTS.WAITLIST_SUBMISSION_SUCCESS, {
          [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
          [PROPERTIES.TIME_TO_SUBMIT]: timeToSubmit,
          response_time: responseTime,
        });

        // Identify user in PostHog (with hashed email for privacy)
        const hashedEmail = hashString(formData.email);
        identifyUser(hashedEmail, {
          email_hash: hashedEmail,
          name_hash: hashString(formData.name),
          has_submitted_waitlist: true,
        });

        // Redirect to checkout page with user details and early access plan
        // Prevent any scroll behavior and navigate immediately
        const checkoutState = {
          plan: {
            id: 1,
            name: 'Early Access',
            price: '₹90',
            period: 'Booking Fee',
            description: 'Secure your early access spot with a ₹90 booking fee',
          },
          userDetails: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
          },
        };
        
        // Store state in sessionStorage as backup (in case navigation state is lost)
        try {
          sessionStorage.setItem('checkoutState', JSON.stringify(checkoutState));
        } catch (e) {
          console.warn('Could not store checkout state in sessionStorage');
        }
        
        // Navigate immediately - don't wait for anything
        // This should happen synchronously to prevent any scroll behavior
        navigate('/checkout', {
          replace: true,
          state: checkoutState,
        });
        
        // Prevent any scroll to the form/button
        // Remove focus from any active element
        if (document.activeElement) {
          document.activeElement.blur();
        }
        
        // Force scroll to top immediately
        window.scrollTo(0, 0);
      } else {
        console.log('API returned unsuccessful response:', response.data);
        setSubmitStatus('error');
        setIsSubmitting(false); // Reset submitting state
        setErrorMessage(response.data?.message || 'Something went wrong. Please try again.');
        
        captureEvent(EVENTS.WAITLIST_SUBMISSION_ERROR, {
          [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.API_ERROR,
          [PROPERTIES.ERROR_MESSAGE]: response.data?.message || 'Unknown error',
          [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
          response_time: responseTime,
        });
      }
    } catch (error) {
      console.error('Error submitting waitlist form:', error);
      setSubmitStatus('error');
      setIsSubmitting(false); // Reset submitting state on error
      const responseTime = Math.round((Date.now() - submitStartTime) / 1000);
      
      // Handle different error types
      if (error.response) {
        console.error('API Error Response:', error.response.status, error.response.data);
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 409) {
          // Conflict - email already exists
          setErrorMessage(errorData.detail || 'This email is already registered on the waitlist.');
          captureEvent(EVENTS.WAITLIST_DUPLICATE_EMAIL, {
            [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.DUPLICATE_EMAIL,
            [PROPERTIES.ERROR_MESSAGE]: errorData.detail || 'Duplicate email',
            [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
            response_time: responseTime,
          });
        } else if (status === 400) {
          // Bad Request - validation error
          setErrorMessage(errorData.detail || 'Please check your information and try again.');
          captureEvent(EVENTS.WAITLIST_SUBMISSION_ERROR, {
            [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.VALIDATION_ERROR,
            [PROPERTIES.ERROR_MESSAGE]: errorData.detail || 'Validation error',
            [PROPERTIES.ERROR_STATUS_CODE]: status,
            [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
            response_time: responseTime,
          });
        } else if (status === 500) {
          // Server error
          setErrorMessage('Server error. Please try again later.');
          captureEvent(EVENTS.WAITLIST_SUBMISSION_ERROR, {
            [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.SERVER_ERROR,
            [PROPERTIES.ERROR_MESSAGE]: 'Server error',
            [PROPERTIES.ERROR_STATUS_CODE]: status,
            [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
            response_time: responseTime,
          });
        } else {
          // Other error status
          setErrorMessage(errorData.detail || errorData.message || 'Failed to submit. Please try again.');
          captureEvent(EVENTS.WAITLIST_SUBMISSION_ERROR, {
            [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.API_ERROR,
            [PROPERTIES.ERROR_MESSAGE]: errorData.detail || errorData.message || 'Unknown error',
            [PROPERTIES.ERROR_STATUS_CODE]: status,
            [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
            response_time: responseTime,
          });
        }
      } else if (error.request) {
        // Request made but no response received (network error)
        console.error('Network error - no response received:', error.request);
        setErrorMessage('Failed to connect to the server. Please check your internet connection and try again.');
        captureEvent(EVENTS.WAITLIST_SUBMISSION_ERROR, {
          [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.NETWORK_ERROR,
          [PROPERTIES.ERROR_MESSAGE]: 'Network error - no response',
          [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
          response_time: responseTime,
        });
      } else {
        // Error setting up the request
        console.error('Error setting up request:', error.message, error);
        setErrorMessage('Failed to submit. Please try again later.');
        captureEvent(EVENTS.WAITLIST_SUBMISSION_ERROR, {
          [PROPERTIES.ERROR_TYPE]: ERROR_TYPES.API_ERROR,
          [PROPERTIES.ERROR_MESSAGE]: error.message || 'Request setup error',
          [PROPERTIES.ATTEMPT_NUMBER]: attemptNumberRef.current,
          response_time: responseTime,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="py-24 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>
      <div className="absolute top-1/2 left-10 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-float"></div>
      <div className="absolute bottom-1/2 right-10 w-96 h-96 bg-yellow-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-float" style={{ animationDelay: '2s' }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Join the <span className="text-gradient">early access</span> list
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Be among the first to experience Snapcom. Get exclusive early access, special launch pricing, and help shape the future of the product.
            </p>
          </div>

          <div className="glass rounded-3xl p-8 md:p-12 animate-fade-in-up stagger-2">
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <CheckCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
                <h3 className="text-3xl font-bold text-white mb-4">You're on the list!</h3>
                <p className="text-gray-400 text-lg mb-8">
                  Thank you for joining! We'll send you an email with your early access details soon.
                </p>
                <Button
                  onClick={() => setSubmitStatus(null)}
                  variant="outline"
                  className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                >
                  Add Another Person
                </Button>
              </div>
            ) : (
              <form 
                action=""
                onSubmit={handleSubmit} 
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white text-base">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => handleFieldFocus(FORM_FIELDS.NAME)}
                      disabled={isSubmitting}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:ring-yellow-500 h-12"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white text-base">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFieldFocus(FORM_FIELDS.EMAIL)}
                      disabled={isSubmitting}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:ring-yellow-500 h-12"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white text-base">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => handleFieldFocus(FORM_FIELDS.PHONE)}
                    disabled={isSubmitting}
                    className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:ring-yellow-500 h-12"
                    required
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="flex items-center space-x-2 text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>{errorMessage || 'Please check your information and try again.'}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg py-6 transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Joining Waitlist...
                    </>
                  ) : (
                    'Get Early Access'
                  )}
                </Button>

                <p className="text-gray-500 text-sm text-center">
                  By signing up, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            )}
          </div>

          {/* Benefits of early access */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6 glass rounded-2xl animate-fade-in-up stagger-3">
              <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-white font-semibold mb-2">Launch Discount</h4>
              <p className="text-gray-400 text-sm">Get 50% off Pro plan for the first 6 months</p>
            </div>
            <div className="text-center p-6 glass rounded-2xl animate-fade-in-up stagger-4">
              <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-white font-semibold mb-2">Early Access</h4>
              <p className="text-gray-400 text-sm">Be the first to use new features</p>
            </div>
            <div className="text-center p-6 glass rounded-2xl animate-fade-in-up stagger-5">
              <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h4 className="text-white font-semibold mb-2">Shape the Product</h4>
              <p className="text-gray-400 text-sm">Your feedback directly influences development</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};